import { useEffect, useState } from 'react';
import { LuCheck, LuLoader2 } from 'react-icons/lu';
import { EtapaRelatorio, ReportJobStatus } from '../api/waitReportJob';

// As etapas que o backend emite, na ordem em que acontecem. Listar aqui é o que permite
// mostrar o que ainda vem pela frente, e não só o que já passou: sem isso a tela não teria
// como saber que depois da sazonalidade vem o maior giro, e quem espera não faria ideia de
// quanto falta.
//
// Se o backend mudar um rótulo, a etapa desconhecida ainda aparece (vem da resposta); o que
// se perde é só a antecipação das seguintes. Por isso a comparação é por prefixo: o maior
// giro chega como "Gerando maior giro (44601 produtos)", com a contagem real embutida.
//
// Uma lista POR TIPO de relatório. Quando havia uma só, a remarcação prometia "Gerando maior
// giro", "Calculando comparacoes com anos anteriores" e "Calculando cubagem e estoque ideal" -
// três etapas que ela nunca executa, e que ficavam pendentes na tela até o fim, dando a
// impressão de relatório interrompido.
const ETAPAS_POR_TIPO: Record<string, string[]> = {
    AAnteriores: [
        'Carregando dados do banco',
        'Organizando dados',
        'Calculando sazonalidade',
        'Gerando maior giro',
        'Calculando comparações com anos anteriores',
        'Calculando cubagem e estoque ideal',
        'Agregando relatório',
    ],
    Rem: [
        'Carregando dados do banco',
        'Organizando dados',
        'Calculando sazonalidade',
        'Calculando cobertura por produto (30 dias)',
        'Calculando cobertura por produto (60 dias)',
        'Calculando índices de lucro',
        'Sugerindo os novos preços',
        'Agregando relatório',
    ],
};

// A lista acima é uma cópia manual dos rótulos que o backend emite, e as duas metades ficam
// em repositórios com deploy separado. Comparar sem acento e sem caixa é o que impede que um
// acento acrescentado de um lado só - ou um deploy chegando antes do outro - faça cada etapa
// aparecer duas vezes, concluída e pendente ao mesmo tempo.
function semAcento(texto: string) {
    return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

export function formatarDuracao(ms: number) {
    const totalSegundos = Math.max(Math.floor(ms / 1000), 0);
    const minutos = Math.floor(totalSegundos / 60);
    const segundos = totalSegundos % 60;

    return `${String(minutos).padStart(2, '0')}:${String(segundos).padStart(2, '0')}`;
}

/**
 * Onde o relatorio esta: etapas previstas que faltam, a em curso, se terminou e a
 * porcentagem. Usada pela tela de progresso e pelo aviso do cabecalho, que acompanha
 * a geracao de qualquer tela do site.
 */
export function resumirProgresso(etapas: EtapaRelatorio[], tipo?: string, concluido?: boolean) {
    const feitas = etapas.map(e => e.label);
    const emAndamento = etapas.find(e => !e.finishedAt);

    // Enquanto o tipo não chegou (a primeira consulta ainda está a caminho) não há o que
    // antecipar: melhor uma lista curta que cresce do que prometer etapas de outro relatório.
    const previstas = tipo ? ETAPAS_POR_TIPO[tipo] ?? [] : [];

    // Junta o que já aconteceu com o que ainda não começou, sem repetir: as previstas que já
    // apareceram na resposta saem da lista de pendentes.
    const pendentes = previstas.filter(
        prevista => !feitas.some(feita => semAcento(feita).startsWith(semAcento(prevista)))
    );

    // Duas origens porque o servidor grava em duas escritas: primeiro fecha a última etapa,
    // depois marca o job como `done`. Uma consulta que caia entre as duas vê a lista inteira
    // fechada com o status ainda `running` - e a tela ficaria sem nada em curso, parecendo
    // travada justamente no fim. Qualquer um dos dois sinais já basta.
    const terminou = Boolean(concluido) || (etapas.length > 0 && !emAndamento && pendentes.length === 0);

    // A barra conta etapas, nao tempo: as etapas duram muito diferente uma da outra, e
    // uma estimativa por tempo prometeria um fim que o servidor nao garante.
    const concluidas = etapas.filter(e => e.finishedAt).length;
    const total = Math.max(concluidas + (emAndamento ? 1 : 0) + pendentes.length, 1);
    const percentual = terminou ? 100 : Math.round((concluidas / total) * 100);

    const atual = emAndamento?.label ?? (terminou ? undefined : pendentes[0]);

    return { emAndamento, pendentes, terminou, concluidas, total, percentual, atual };
}

type Props = {
    etapas: EtapaRelatorio[];
    iniciadoEm: number | null;
    retomado: boolean;
    /** ReportTypes do backend. Decide quais etapas a tela antecipa. */
    tipo?: string;
    /** Job já marcado como `done` pelo servidor. Ver `concluido` abaixo. */
    concluido?: boolean;
    /** Estado do job no servidor. `queued` = a task do relatório ainda está subindo. */
    status?: ReportJobStatus;
};

export function ProgressoRelatorio({ etapas, iniciadoEm, retomado, tipo, concluido, status }: Props) {
    // O relógio precisa andar entre uma consulta e outra: as etapas só chegam de 3 em 3
    // segundos, mas um cronômetro que pula de 3 em 3 parece travado. Este tick é só visual.
    const [agora, setAgora] = useState(Date.now());

    useEffect(() => {
        const id = setInterval(() => setAgora(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    const { emAndamento, pendentes, terminou, percentual } = resumirProgresso(etapas, tipo, concluido);

    return (
        <div className="progresso-relatorio">
            <div className="progresso-relatorio-header">
                <div>
                    <span className="progresso-relatorio-rotulo">
                        {retomado ? 'Acompanhamento retomado' : 'Em processamento'}
                    </span>
                    <span className="progresso-relatorio-titulo">
                        {tipo === 'Rem' ? 'Gerando remarcação' : 'Gerando relatório'}
                    </span>
                </div>
                <span className="progresso-relatorio-tempo" title="Tempo decorrido">
                    {iniciadoEm ? formatarDuracao(agora - iniciadoEm) : '--:--'}
                </span>
            </div>

            <div
                className="progresso-relatorio-barra"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percentual}
                aria-label="Etapas concluídas"
            >
                <span style={{ width: `${percentual}%` }} />
            </div>

            <p className="progresso-relatorio-aviso">
                O cálculo roda no servidor e leva alguns minutos. Se quiser, pode fechar a página, o
                relatório continua sendo gerado.
            </p>

            <ul className="progresso-relatorio-lista">
                {etapas.map((etapa, i) => {
                    const concluida = Boolean(etapa.finishedAt);
                    const duracao = concluida
                        ? new Date(etapa.finishedAt as string).getTime() - new Date(etapa.startedAt).getTime()
                        : agora - new Date(etapa.startedAt).getTime();

                    return (
                        <li key={`${etapa.label}-${i}`} className={concluida ? 'etapa concluida' : 'etapa atual'}>
                            <span className="etapa-marcador">{concluida ? <LuCheck /> : <LuLoader2 className="etapa-girando" />}</span>
                            <span className="etapa-label">{etapa.label}</span>
                            <span className="etapa-duracao">{formatarDuracao(duracao)}</span>
                        </li>
                    );
                })}

                {pendentes.map(label => (
                    <li key={label} className="etapa pendente">
                        <span className="etapa-marcador"><span className="etapa-ponto" /></span>
                        <span className="etapa-label">{label}</span>
                        <span className="etapa-duracao" />
                    </li>
                ))}
            </ul>

            {/*
              * O cálculo acabou, mas a tela do relatório ainda vai buscar e montar a árvore -
              * são dezenas de milhares de linhas, e isso leva alguns segundos em que nada
              * acontecia na tela. Sem esta linha a lista terminava e a página parecia ter
              * parado. A mesma frase continua no lugar do spinner da página de relatório.
              */}
            {terminou && (
                <p className="progresso-relatorio-final">
                    Relatório concluído. Organizando os dados para mostrar na tela…
                </p>
            )}

            {/*
              * Dois vãos diferentes que até aqui mostravam a mesma frase. Com o cálculo em
              * container próprio, `queued` quer dizer Fargate provisionando a task, baixando
              * a imagem e subindo o Node: 30 a 60 segundos, contra os ~3 do worker thread.
              * Uma frase dimensionada para 3 segundos segurando a tela por um minuto é o que
              * faz parecer travado — e, se o container não subir, é esta linha que fica de pé
              * pelos três minutos do `JOB_SEM_CONTAINER_MS` até o servidor condenar o job.
              */}
            {!emAndamento && etapas.length === 0 && (
                <p className="progresso-relatorio-aviso">
                    {status === 'queued'
                        ? 'Preparando o ambiente de execução…'
                        : 'Iniciando o processamento…'}
                </p>
            )}
        </div>
    );
}
